import { Alert, Col, Flex, InputNumber, Radio, Row } from 'antd';
import { useSelected } from '../../../../../providers/selectedContext';
import RefinementChart from './chart';
import CoreyExponentsTable from './coreyExpoentsTable';
import RefinementTable from './table';
import { useAppContext } from '../../../../../providers/AppContext';
import { useEffect, useState } from 'react';
import { Accordion } from '../../../../../design-system/accordion';
import { useTheme } from '../../../../../providers/theme';
import { getColors } from '../../../../utils/getColors';
import { calculateKroKrgIntersection } from './krIntersection';
import { verifyValue } from '../../../specialCore';
import {
  CorrelationComparisonMode,
  CorrelationEndpointSource
} from '../../../../../@types/projects/types';
import {
  ENDPOINT_COMPARISON_MODE_LABELS,
  ENDPOINT_SOURCE_LABELS
} from '../../../../../design-system/correlation/utils/endpointSourceConstants';
import { useCorrelation } from '../../../../../providers/Correlation';
import {
  buildGasOilRefinementCurves,
  clampCurvePointCount,
  MAX_CURVE_POINTS,
  MIN_CURVE_POINTS
} from '../../../../utils/curveGeneration';
import { resolveGasOilValues } from './valueResolver';

interface ChartData {
  /** Stable id so Highcharts matches series by id (not array index) across
   *  in-place updates — required since overlay mode prepends series. */
  id: string;
  name: string;
  data: number[][];
  color: string;
  lineWidth: number;
  marker?: {
    symbol?: string;
    enabled: boolean;
  };
}

const format5 = (value: number | string | null | undefined) => {
  if (value == null) {
    return verifyValue(value as any);
  }

  const numeric = typeof value === 'number' ? value : Number(value);
  if (Number.isNaN(numeric)) {
    return verifyValue(value as any);
  }

  return numeric.toFixed(5);
};

const SOURCE_ALERT_COLORS: Record<CorrelationEndpointSource, string> = {
  lab: '#8a8a8a',
  refinement: '#3178f6'
};

const GasOilRefinement = () => {
  const { selectedInfo } = useSelected();
  const { isDay } = useTheme();
  const colors = getColors(isDay);
  const { updateConfigStates } = useAppContext();
  const [chartData, setChartData] = useState<ChartData[]>([]);
  const sampleCurves = selectedInfo?.sample?.input_sample_curves?.gas_oil;
  const kRelCurves = selectedInfo?.sample?.k_rel_curves?.gas_oil;
  const [endpointSource, setEndpointSource] =
    useState<CorrelationEndpointSource>('lab');
  const [comparisonMode, setComparisonMode] =
    useState<CorrelationComparisonMode>('off');
  const [numberOfPoints, setNumberOfPoints] = useState(30);
  const { appliedEndpointSource } = useCorrelation();

  const sgKrel = sampleCurves?.sg_krel ?? [];
  const labResolved = resolveGasOilValues(selectedInfo?.sample, 'lab');
  const refinementResolved = resolveGasOilValues(selectedInfo?.sample, 'refinement');
  const no_g = labResolved.no ?? 0;
  const ng = labResolved.ng ?? 0;
  const krgEndpoint = labResolved.krgEndpoint ?? 0;

  const labSon = kRelCurves?.son ?? [];
  const labSgn = kRelCurves?.sgn ?? [];
  const labKro = labSon.map(s => (s !== null ? Math.pow(Math.max(s, 0), no_g) : null));
  const labKrg = labSgn.map(s => (s !== null ? krgEndpoint * Math.pow(Math.max(s, 0), ng) : null));

  const labGenerated = buildGasOilRefinementCurves({
    swi: labResolved.swi ?? 0,
    sgc: labResolved.sgc ?? 0,
    sor: labResolved.sor ?? 0,
    no: labResolved.no ?? 0,
    ng: labResolved.ng ?? 0,
    krgEndpoint: labResolved.krgEndpoint ?? 0,
    kroEndpoint: labResolved.kroEndpoint,
    normSor: labResolved.normSor ?? undefined,
    numberOfPoints
  });
  const refinementGenerated = buildGasOilRefinementCurves({
    swi: refinementResolved.swi ?? 0,
    sgc: refinementResolved.sgc ?? 0,
    sor: refinementResolved.sor ?? 0,
    no: refinementResolved.no ?? 0,
    ng: refinementResolved.ng ?? 0,
    krgEndpoint: refinementResolved.krgEndpoint ?? 0,
    kroEndpoint: refinementResolved.kroEndpoint,
    normSor: refinementResolved.normSor ?? undefined,
    numberOfPoints
  });

  const getSourceValues = (source: CorrelationEndpointSource) => {
    if (source === 'refinement') {
      return {
        sg: refinementGenerated.sg,
        krg: refinementGenerated.krg,
        kro: refinementGenerated.kro
      };
    }
    return {
      sg: labGenerated.sg.length ? labGenerated.sg : sgKrel,
      krg: labGenerated.krg.length ? labGenerated.krg : labKrg,
      kro: labGenerated.kro.length ? labGenerated.kro : labKro
    };
  };

  const selectedValues = getSourceValues(endpointSource);
  const getValidationMessages = (
    source: CorrelationEndpointSource
  ): string[] => {
    const sourceValues = getSourceValues(source);
    const sgValues = sourceValues.sg ?? [];
    const resolved = resolveGasOilValues(selectedInfo?.sample, source);
    const sorgValue = resolved.sor;
    const krgEndpoint = resolved.krgEndpoint;
    const sourceLabel = source === 'lab' ? 'Lab' : 'Refinement';
    const warnings: string[] = [];

    const intersectionStatus = calculateKroKrgIntersection({
      sgValues,
      swi: resolved.swi ?? sgValues?.[0],
      sgc: resolved.sgc,
      sor: sorgValue,
      no: resolved.no,
      ng: resolved.ng,
      krgEndPoint: krgEndpoint
    });

    if (intersectionStatus.status === 'invalid-input') {
      warnings.push(
        `[${sourceLabel}] Invalid inputs for refinement calculations. Check Swi/Sgc/Sor values.`
      );
    } else if (intersectionStatus.invalidRange) {
      warnings.push(
        `[${sourceLabel}] Some Sg values are outside the valid range [Sgc, 1 - Swi - Sor].`
      );
    }

    const finiteSgValues = sgValues.filter(v => v != null);
    const maxSg = finiteSgValues.length > 0 ? Math.max(...finiteSgValues) : null;
    const swi = resolved.swi;
    const expectedSorg = maxSg != null && swi != null ? 1 - swi - maxSg : null;
    if (
      expectedSorg != null &&
      sorgValue != null &&
      Math.abs(expectedSorg - sorgValue) > 1e-4
    ) {
      warnings.push(
        `[${sourceLabel}] Sorg consistency check failed: Sorg=${sorgValue.toFixed(
          4
        )}, expected (1 - Swi - max(Sg))=${expectedSorg.toFixed(4)}.`
      );
    }

    return warnings;
  };

  const validationMessages = [
    ...getValidationMessages('lab'),
    ...getValidationMessages('refinement')
  ];

  useEffect(() => {
    updateConfigStates({ showRefinementTable: true });
  }, []);

  const tableColumns = [
    { title: `Sg`, dataIndex: 'Sg', key: 'Sg', render: format5 },
    { title: `Krg Scaled`, dataIndex: 'Krg', key: 'Krg', render: format5 },
    { title: `Kro Scaled`, dataIndex: 'Kro', key: 'Kro', render: format5 }
  ];

  const tableData = selectedValues.sg?.map((sg, index) => ({
    Sg: sg,
    Krg: selectedValues.krg?.[index],
    Kro: selectedValues.kro?.[index]
  }));

  const getChartData = () => {
    const kroScaledData: number[][] = [];
    const krgScaledData: number[][] = [];
    const kroLabData: number[][] = [];
    const krgLabData: number[][] = [];

    const sg = selectedValues.sg ?? [];
    const kroScaled = selectedValues.kro ?? [];
    const krgScaled = selectedValues.krg ?? [];

    const secondarySource: CorrelationEndpointSource =
      endpointSource === 'lab' ? 'refinement' : 'lab';
    const secondaryValues = getSourceValues(secondarySource);
    const secondaryKroData: number[][] = [];
    const secondaryKrgData: number[][] = [];

    const sgKrel = sampleCurves?.sg_krel ?? [];
    const kroLab = sampleCurves?.kro_lab ?? [];
    const krgLab = sampleCurves?.krg_lab ?? [];

    if (sg.length && kroScaled.length && krgScaled.length) {
      sg.forEach((_, index) => {
        const kroVal = kroScaled[index];
        const krgVal = krgScaled[index];
        if (kroVal != null) kroScaledData.push([sg[index], kroVal]);
        if (krgVal != null) krgScaledData.push([sg[index], krgVal]);
      });
    }

    if (sgKrel.length && kroLab.length && krgLab.length) {
      sgKrel.forEach((_, index) => {
        kroLabData.push([sgKrel[index], kroLab[index]]);
        krgLabData.push([sgKrel[index], krgLab[index]]);
      });
    }

    const currentChartData: ChartData[] = [
      {
        id: 'kro-calc',
        name: `Kro (${ENDPOINT_SOURCE_LABELS[endpointSource]})`,
        data: kroScaledData,
        color: '#059e05',
        lineWidth: 3,
        marker: { enabled: false }
      },
      {
        id: 'krg-calc',
        name: `Krg (${ENDPOINT_SOURCE_LABELS[endpointSource]})`,
        data: krgScaledData,
        color: '#fa3c3c',
        lineWidth: 3,
        marker: { enabled: false }
      },
      {
        id: 'kro-lab',
        name: 'Kro Lab',
        data: kroLabData,
        color: '#059e05',
        lineWidth: 0,
        marker: {
          symbol: 'circle',
          enabled: true
        }
      },
      {
        id: 'krg-lab',
        name: 'Krg Lab',
        data: krgLabData,
        color: '#fa3c3c',
        lineWidth: 0,
        marker: {
          symbol: 'circle',
          enabled: true
        }
      }
    ];

    if (comparisonMode === 'overlay') {
      (secondaryValues.sg ?? []).forEach((_, index) => {
        const sgValue = secondaryValues.sg?.[index];
        const kroValue = secondaryValues.kro?.[index];
        const krgValue = secondaryValues.krg?.[index];
        if (sgValue != null && kroValue != null) {
          secondaryKroData.push([sgValue, kroValue]);
        }
        if (sgValue != null && krgValue != null) {
          secondaryKrgData.push([sgValue, krgValue]);
        }
      });

      currentChartData.unshift(
        {
          id: 'kro-calc-secondary',
          name: `Kro (${ENDPOINT_SOURCE_LABELS[secondarySource]})`,
          data: secondaryKroData,
          color: 'rgba(120,120,120,0.45)',
          lineWidth: 2,
          marker: { enabled: false }
        },
        {
          id: 'krg-calc-secondary',
          name: `Krg (${ENDPOINT_SOURCE_LABELS[secondarySource]})`,
          data: secondaryKrgData,
          color: 'rgba(120,120,120,0.30)',
          lineWidth: 2,
          marker: { enabled: false }
        }
      );
    }

    setChartData(currentChartData);
  };

  useEffect(() => {
    getChartData();
  }, [selectedInfo, endpointSource, comparisonMode, numberOfPoints]);

  useEffect(() => {
    setEndpointSource(appliedEndpointSource);
  }, [appliedEndpointSource]);

  return (
    <Accordion defaultOpen="refinement-gas-oil">
      <Accordion.Item
        id="refinement-gas-oil"
        header={
          <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%', fontWeight: 'bold' }}>
            <span>Refinement</span>
            <span style={{ color: colors.gasOil }}>Gas-Oil</span>
          </div>
        }
      >
        {kRelCurves?.son?.length ? (
          <div style={{ padding: '0.5rem' }}>
            <div style={{ display: 'flex', marginBottom: '0.5rem' }}>
              <Radio.Group
                optionType="button"
                buttonStyle="solid"
                value={endpointSource}
                onChange={event => {
                  const nextSource = event.target.value as CorrelationEndpointSource;
                  setEndpointSource(nextSource);
                }}
                style={{ marginRight: '0.5rem' }}
              >
                <Radio.Button value="lab">
                  {ENDPOINT_SOURCE_LABELS.lab}
                </Radio.Button>
                <Radio.Button value="refinement">
                  {ENDPOINT_SOURCE_LABELS.refinement}
                </Radio.Button>
              </Radio.Group>
              <Radio.Group
                optionType="button"
                buttonStyle="solid"
                value={comparisonMode}
                onChange={event => {
                  const nextMode = event.target.value as CorrelationComparisonMode;
                  setComparisonMode(nextMode);
                }}
              >
                <Radio.Button value="off">
                  {ENDPOINT_COMPARISON_MODE_LABELS.off}
                </Radio.Button>
                <Radio.Button value="overlay">
                  {ENDPOINT_COMPARISON_MODE_LABELS.overlay}
                </Radio.Button>
              </Radio.Group>
              <Flex align="center" gap="small" style={{ marginLeft: '0.5rem' }}>
                <span style={{ fontSize: '12px' }}>Curve points:</span>
                <InputNumber
                  min={MIN_CURVE_POINTS}
                  max={MAX_CURVE_POINTS}
                  step={10}
                  value={numberOfPoints}
                  onChange={value => {
                    if (value != null) {
                      setNumberOfPoints(clampCurvePointCount(value));
                    }
                  }}
                  size="small"
                  style={{ width: 72 }}
                />
              </Flex>
            </div>
            {validationMessages.map(messageText => {
              const source: CorrelationEndpointSource = messageText.includes(
                '[Refinement]'
              )
                ? 'refinement'
                : 'lab';
              return (
                <Alert
                  key={messageText}
                  message={messageText}
                  type="warning"
                  showIcon
                  style={{
                    marginBottom: '0.5rem',
                    borderLeft: `3px solid ${SOURCE_ALERT_COLORS[source]}`
                  }}
                />
              );
            })}
            <Row>
              <Col span={7}>
                <RefinementTable
                  columns={tableColumns}
                  data={tableData}
                  bordered={false}
                />
              </Col>
              <Col span={6}>
                <CoreyExponentsTable endpointSource={endpointSource} />
              </Col>
              <Col span={11}>
                <RefinementChart
                  data={chartData}
                  endpointSource={endpointSource}
                  comparisonMode={comparisonMode}
                />
              </Col>
            </Row>
          </div>
        ) : (
          <div className="no-data-div">No refinement curves generated yet. Click <strong>Calculate</strong> in the Laboratory Data section to compute Corey exponents and refinement curves.</div>
        )}
      </Accordion.Item>
    </Accordion>
  );
};

export default GasOilRefinement;
